import React, { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

import { Button, CreateTableNew as CreateTable } from "../../../common/APICommon";
import { AddEditVesselFunctionModal } from "../shared/AddEditVesselFunctionModal";


/**
 * Tab 3 — Vessel Function Definition
 *
 * MODIFIED: Brought back the table (1:Many).
 * - Add button opens modal with VesselFunction dropdown + IsActive toggle
 * - Edit button opens modal to toggle IsActive only
 * - No delete — IsActive is used instead
 *
 * Table columns: Line #, Function Code, Function Description, Is Active, Actions
 * Rows stored in form.vessel201Functions (array).
 */
export const VesselFunctionDefinitionTab = ({ form, set, options, isViewOnly, errors = {} }) => { // MODIFIED: added errors prop for consistency
  const rows = form.vessel201Functions || [];
  const editable = !isViewOnly;

  const [currentPage, setCurrentPage] = useState(1);

  // ── Modal state ───────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedRow, setSelectedRow] = useState(null);

  const updateRows = (updated) => set("vessel201Functions", updated);

  // ── Open Add ──────────────────────────────────────────────────────────────
  const openAdd = () => {
    setModalMode("add");
    setSelectedRow(null);
    setShowModal(true);
  };

  // ── Open Edit ─────────────────────────────────────────────────────────────
  const openEdit = (row) => {
    setModalMode("edit");
    setSelectedRow(row);
    setShowModal(true);
  };

  // ── Handlers passed to modal ──────────────────────────────────────────────
  const handleAdd = (rowForm) => {
    const match = (options?.vesselFunctions || []).find(
      (v) => String(v.value) === String(rowForm.vesselFunctionId)
    );
    updateRows([
      ...rows,
      {
        id: null,
        vesselFunctionId: rowForm.vesselFunctionId,
        functionCode: match?.label || "",
        functionDescription: match?.vesselFunctionDescription || "",
        isActive: rowForm.isActive,
      },
    ]);
  };

  const handleUpdate = (rowForm, index) => {
    const match = (options?.vesselFunctions || []).find(
      (v) => String(v.value) === String(rowForm.vesselFunctionId)
    );
    updateRows(
      rows.map((r, i) =>
        i === index
          ? {
            ...r,
            vesselFunctionId: rowForm.vesselFunctionId,
            functionCode: match?.label || r.functionCode,
            functionDescription: match?.vesselFunctionDescription || r.functionDescription,
            isActive: rowForm.isActive,
          }
          : r
      )
    );
  };

  // ── Table config ──────────────────────────────────────────────────────────
  const headers = [
    "Line #",
    "Function Code",
    "Function Description",
    "Is Active",
    ...(editable ? ["Actions"] : []),
  ];
  const accessors = [
    "lineNumber",
    "functionCode",
    "functionDescription",
    "isActiveDisplay",
    ...(editable ? ["actions"] : []),
  ];

  const tableData = rows.map((row, index) => ({
    ...row,
    lineNumber: index + 1,
    _index: index,
    isActiveDisplay: row.isActive
      ? <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">Active</span>
      : <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">Inactive</span>,
  }));

  const actions = editable
    ? [
      {
        name: "Edit",
        icon: <EditIcon fontSize="small" />,
        className: "bg-green-500 hover:bg-green-600 flex items-center",
        function: (row) => openEdit(row),
        visible: true,
      },
    ]
    : [];

  return (
    <div className="space-y-4 pt-2">
      <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-gray-500">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
            Vessel Function Definition
          </span>
          {editable && (
            <Button
              icon={<AddIcon />}
              name="Add Row"
              className="bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"
              onClick={openAdd}
            />
          )}
        </div>

        <CreateTable
          tableName="VesselFunctionDefinitionTable"
          headers={headers}
          accessors={accessors}
          data={tableData}
          actions={actions}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={10}
          totalItems={tableData.length}
        />
      </div>

      <AddEditVesselFunctionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        mode={modalMode}
        selectedData={selectedRow}
        options={options}
        onAdd={handleAdd}
        onUpdate={handleUpdate}
      />
    </div>
  );
};